import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Home, FileCheck, DollarSign, Users, ChevronRight, CheckCircle2, Menu, X } from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { PreferencesDialog } from "../components/Preferences";

export default function LandingPage() {
  const navigate = useNavigate();
  const [rentAmount, setRentAmount] = useState("");
  const [premium, setPremium] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const calculatePremium = () => {
    const rent = parseFloat(rentAmount);
    if (rent > 0) {
      const monthlyPremium = (rent * 0.015).toFixed(2);
      const signupFee = 50.00;
      setPremium({
        monthlyPremium,
        signupFee,
        total: (parseFloat(monthlyPremium) + signupFee).toFixed(2)
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="glass-header fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="nav-link">How It Works</a>
              <a href="#coverage" className="nav-link">Coverage</a>
              <a href="#calculator" className="nav-link">Calculator</a>
              <Link to="/faq" className="nav-link">FAQ</Link>
              <Link to="/agent/login" className="nav-link">For Agents</Link>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <PreferencesDialog />
              <Link to="/login">
                <Button variant="ghost" className="text-primary" data-testid="login-btn">Log In</Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary" data-testid="get-started-btn">Get Started</Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-border">
            <div className="px-4 py-4 space-y-3">
              <a href="#how-it-works" className="block py-2 text-foreground">How It Works</a>
              <a href="#coverage" className="block py-2 text-foreground">Coverage</a>
              <a href="#calculator" className="block py-2 text-foreground">Calculator</a>
              <Link to="/faq" className="block py-2 text-foreground">FAQ</Link>
              <Link to="/agent/login" className="block py-2 text-foreground">For Agents</Link>
              <div className="pt-4 space-y-3">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">Log In</Button>
                </Link>
                <Link to="/register" className="block">
                  <Button className="btn-primary w-full">Get Started</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6" style={{ fontFamily: 'Young Serif, serif' }}>
                Protect Your Home <br />
                <span className="text-primary">When Life Happens</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Rent insurance that covers your rent for up to 3 months if you lose your job. 
                Peace of mind for just 1.5% of your monthly rent.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary text-lg px-10 py-6" 
                  onClick={() => navigate('/register')}
                  data-testid="hero-get-quote-btn"
                >
                  Get Your Quote
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  className="btn-outline text-lg px-8 py-6"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="animate-fade-in animate-delay-200 relative">
              <img 
                src="https://images.unsplash.com/photo-1759722668109-0ce25491240a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb20lMjByZWxheGluZ3xlbnwwfHx8fDE3NzEwMDg5MDF8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern calm living room"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">10,000+</p>
                    <p className="text-sm text-muted-foreground">Renters Protected</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
              How TheRentPayer Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Simple, transparent protection for your home in three easy steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FileCheck,
                title: "Get a Quote",
                description: "Tell us about your rent and employment. Get an instant quote with no obligations.",
                step: "01"
              },
              {
                icon: DollarSign,
                title: "Pay Your Premium",
                description: "Small monthly payments - just 1.5% of your rent plus a one-time signup fee.",
                step: "02"
              },
              {
                icon: NestIcon,
                title: "Stay Protected",
                description: "If you lose your job, we'll pay your rent for up to 3 months while you get back on your feet.",
                step: "03"
              }
            ].map((item, idx) => (
              <div key={idx} className="card-organic feature-card p-8 card-hover">
                <div className="text-5xl font-bold text-primary/10 mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
                  {item.step}
                </div>
                <div className="bg-primary/10 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3" style={{ fontFamily: 'Young Serif, serif' }}>
                  {item.title}
                </h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Details */}
      <section id="coverage" className="py-20 px-4 bg-secondary">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6" style={{ fontFamily: 'Young Serif, serif' }}>
                Comprehensive Coverage <br />You Can Count On
              </h2>
              <div className="space-y-6">
                {[
                  { title: "3 Months Coverage", desc: "Up to 3 months of rent payments if you lose your job" },
                  { title: "Quick Claims", desc: "File a claim online and get approved within days" },
                  { title: "Direct Payments", desc: "We pay your landlord directly - no hassle for you" },
                  { title: "No Hidden Fees", desc: "Transparent pricing with no surprise charges" }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="bg-primary rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle2 className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{item.title}</h4>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758874383965-667e4df51310?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb20lMjByZWxheGluZ3xlbnwwfHx8fDE3NzEwMDg5MDF8MA&ixlib=rb-4.1.0&q=85"
                alt="Man relaxing with peace of mind"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Premium Calculator */}
      <section id="calculator" className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
              Calculate Your Premium
            </h2>
            <p className="text-muted-foreground">
              See how affordable rent protection can be
            </p>
          </div>

          <div className="card-organic p-8">
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Your Monthly Rent
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  placeholder="1500"
                  value={rentAmount}
                  onChange={(e) => setRentAmount(e.target.value)}
                  className="pl-8 h-14 text-lg"
                  data-testid="rent-calculator-input"
                />
              </div>
            </div>
            <Button 
              className="btn-primary w-full py-6 text-lg"
              onClick={calculatePremium}
              data-testid="calculate-btn"
            >
              Calculate Premium
            </Button>

            {premium && (
              <div className="mt-8 calculator-result animate-fade-in">
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-white/70 text-sm">Monthly Premium</p>
                    <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Young Serif, serif' }}>
                      ${premium.monthlyPremium}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">One-time Signup Fee</p>
                    <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Young Serif, serif' }}>
                      ${premium.signupFee.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="border-t border-white/20 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white/90">First Payment Total</span>
                    <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Young Serif, serif' }}>
                      ${premium.total}
                    </span>
                  </div>
                </div>
                <Button 
                  className="w-full mt-6 bg-white text-primary hover:bg-white/90 py-6"
                  onClick={() => navigate('/register')}
                  data-testid="get-protected-btn"
                >
                  Get Protected Now
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Young Serif, serif' }}>
            Ready to Protect Your Home?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Join thousands of renters who have peace of mind knowing their rent is protected.
          </p>
          <Button 
            className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 rounded-full"
            onClick={() => navigate('/register')}
            data-testid="cta-get-started-btn"
          >
            Get Started Today
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <NestIcon className="h-6 w-6 text-white" />
                <span className="text-lg font-bold text-white" style={{ fontFamily: 'Young Serif, serif' }}>
                  TheRentPayer
                </span>
              </div>
              <p className="text-white/60 text-sm">
                Protecting renters with affordable insurance coverage since 2024.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><a href="#how-it-works" className="hover:text-white">How It Works</a></li>
                <li><a href="#coverage" className="hover:text-white">Coverage</a></li>
                <li><a href="#calculator" className="hover:text-white">Calculator</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/agent/login" className="hover:text-white">Agent Portal</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                <li><Link to="/support" className="hover:text-white">Contact Us</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/40 text-sm">
            © 2025 TheRentPayer. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
